import React, { useState } from "react";
import {
  Grid,
  Card,
  TextField,
  Button,
  Typography,
  makeStyles,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
  Box,
  InputAdornment,
  Container,
  Fade,
  IconButton,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import MailIcon from "@material-ui/icons/Mail";
import LockIcon from "@material-ui/icons/Lock";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../store/action/auth/authAction";
import { useForm } from "react-hook-form";
import Notify from "../../components/Notify/Notify";
import { apiClient } from "../../config/apiClient";
import registration from "../../assets/images/registration.svg";
import bgLogo from "../../assets/images/bg.svg";
import { Business, Visibility, VisibilityOff } from "@material-ui/icons";
import logo from "../../assets/images/logo.svg";
import updatedLogo from "../../assets/images/updatedLogo.svg";
const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      width: "200%",
      height: "200%",
      top: "-50%",
      left: "-50%",
      zIndex: 0,
      background: `url(${bgLogo})`,
      backgroundSize: "contain",
      opacity: 0.05,
      transform: "rotate(-15deg)",
    },
  },
  container: {
    position: "relative",
    zIndex: 1,
  },
  authCard: {
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
    boxShadow: theme.shadows[8],
    width: "100%",
    padding: 0
  },
  leftPanel: {
    background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}20 100%)`,
    padding: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4),
      borderRight: "none",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  rightPanel: {
    padding: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  form: {
    width: "100%",
  },
  inputIcon: {
    color: theme.palette.primary.main,
  },
  button: {
    borderRadius: theme.shape.borderRadius,
    height: 48,
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: theme.shadows[2],
    "&:hover": {
      boxShadow: theme.shadows[4],
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  link: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
    padding: theme.spacing(0.5),
    display: "inline-block",
  },
  registerLink: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
    padding: theme.spacing(0.5),
    display: "inline-block",
    marginLeft: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(3, 0),
    color: theme.palette.text.secondary,
  },
  textField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
  },
  logo: {
    marginBottom: theme.spacing(3),
    width: 200,
    height: "auto",
  },
  illustration: {
    width: "100%",
    // maxWidth: 340,
    height: "auto",
    marginBottom: theme.spacing(3),
  },
  visibilityToggle: {
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

const Login = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const { loginErrors, userInfo, loading } = useSelector((state) => state.authenticate);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm();

  const [loginNotify, setLoginNotify] = useState(false);
  const [orgList, setOrgList] = useState([]);
  const [emailNotify, setEmailNotify] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const emailValue = watch("email");

  // 🔹 Email blur check using RHF value
  const handleBlurEmail = async () => {
    const email = getValues("email");
    if (email) {
      setEmailNotify("");
      try {
        const response = await apiClient(true).post(
          `api/signee/get-email-organisation`,
          { email }
        );
        if (response.data?.status === true) {
          setOrgList(response.data);
        } else {
          setEmailNotify("Invalid email address");
        }
      } catch (error) {
        setEmailNotify(error.response?.data?.message || "Error checking email");
      }
    }
  };

  // 🔹 Submit with RHF data
  const loginSubmit = (formData) => {
    dispatch(login(formData));
    setLoginNotify(true);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      {loginNotify && (loginErrors?.message || loginErrors) && (
        <Notify data={loginErrors?.message ? loginErrors?.message : loginErrors} status="error" />
      )}

      {emailNotify && <Notify data={emailNotify} status="error" />}

      {loginNotify && userInfo?.message && (<Notify data={userInfo?.message} status="success" />)}

      <Box className={classes.root}>
        <Container maxWidth="lg" className={classes.container}>
          <Card className={classes.authCard}>
            <Grid container alignItems="center">
              {/* Left Section */}
              <Grid item xs={12} md={6} className={classes.leftPanel}>
                <img src={registration} alt="Login Illustration" className={classes.illustration} />
              </Grid>

              {/* Right Section */}
              <Grid item xs={12} md={6} className={classes.rightPanel}>
                <Box style={{ textAlign: "center" }}>
                  <img src={updatedLogo} alt="Logo" className={classes.logo} />
                </Box>
                <Typography variant="h5" gutterBottom color="primary">
                  Sign In
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Enter your details to access your account
                </Typography>
                <form onSubmit={handleSubmit(loginSubmit)} autoComplete="off" className={classes.form}>
                  {/* Email */}
                  <TextField
                    placeholder="Email Address"
                    variant="outlined"
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailIcon className={classes.inputIcon} />
                        </InputAdornment>
                      ),
                    }}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    fullWidth
                    margin="normal"
                    onBlur={handleBlurEmail}
                  />

                  {/* Password */}
                  <TextField
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon className={classes.inputIcon} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                            className={classes.visibilityToggle}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    fullWidth
                    margin="normal"
                  />

                  {/* Organization */}
                  {emailValue && orgList?.data && orgList.data.length > 0 && (
                    <Fade in={true}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        className={classes.textField}
                        error={!!errors.organization_id}
                      >
                        <InputLabel>Organization</InputLabel>
                        <Select
                          label="Organization"
                          {...register("organization_id", {
                            required: "Please select an organization",
                          })}
                          startAdornment={
                            <InputAdornment position="start">
                              <Business
                                className={classes.inputIcon}
                                style={{ marginRight: 8 }}
                              />
                            </InputAdornment>
                          }
                        >
                          <MenuItem value="">
                            <em>Select Organization</em>
                          </MenuItem>
                          {orgList.data.map((list, index) => (
                            <MenuItem value={list.organization_id} key={index}>
                              {list.organization_name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.organization_id && (
                          <Typography variant="caption" color="error">
                            {errors.organization_id.message}
                          </Typography>
                        )}
                      </FormControl>
                    </Fade>
                  )}

                  {/* Login Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>

                  <Divider className={classes.divider} />

                  {/* Footer Links */}
                  <Box className={classes.footer}>
                    <Link
                      to="/forgotten-password"
                      className={classes.link}
                    >
                      Forgot Password?
                    </Link>
                    <Typography variant="body2">
                      Don't have an account?{" "}
                      <Link to="/register" className={classes.registerLink}>
                        Register
                      </Link>
                    </Typography>
                  </Box>
                </form>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default Login;